// src/js/api-bnf.js
// Interroge le catalogue de la BnF via le protocole SRU en Dublin Core.
// Retourne un objet normalisé { title, authors, publisher, series, isbn, coverUrl }
// ou null si rien n'est trouvé.

const BNF_SRU_BASE = 'https://catalogue.bnf.fr/api/SRU';
const BNF_COVER_BASE = 'https://catalogue.bnf.fr/couverture';

/**
 * @type {BookApiAdapter}
 */
export const bnfAdapter = {
    name: 'Catalgue BNF',

    async fetch(isbn) {
        const url = _buildUrl(isbn);

        let xmlDoc;
        try {
            const res = await fetch(url);
            if (!res.ok) return null;
            const text = await res.text();
            xmlDoc = new DOMParser().parseFromString(text, 'application/xml');
        } catch (err) {
            console.warn('BnF SRU — erreur réseau :', err);
            return null;
        }

        // Vérifie qu'on a au moins un résultat
        const numberOfRecords = xmlDoc.querySelector('numberOfRecords')?.textContent?.trim();
        if (!numberOfRecords || numberOfRecords === '0') return null;

        // On prend la première notice
        const dc = xmlDoc.querySelector('dc');
        if (!dc) return null;

        return {
            title: _extractTitle(dc),
            authors: _extractAuthors(dc),
            publisher: _extractPublisher(dc),
            series: _extractSeries(dc),
            isbn,
            coverUrl: _extractCoverUrl(dc, isbn),
        };
    }
}

// --- Construction de l'URL ---

function _buildUrl(isbn) {
    const query = `bib.isbn adj "${isbn}"`;
    const params = new URLSearchParams({
        version: '1.2',
        operation: 'searchRetrieve',
        query,
        maximumRecords: '1',
        recordSchema: 'dublincore',
    });
    return `${BNF_SRU_BASE}?${params}`;
}

// --- Extraction des champs Dublin Core ---

/**
 * Récupère tous les éléments d'un tag DC dans une notice.
 * @param {Element} dc
 * @param {string}  tag  ex: 'title', 'creator', 'publisher'
 * @returns {string[]}
 */
function _getAll(dc, tag) {
    return [...dc.querySelectorAll(tag)]
        .map(el => el.textContent.trim())
        .filter(Boolean);
}

function _extractTitle(dc) {
    // La BnF inclut parfois le numéro de tome dans le titre : "Astérix le Gaulois. 1"
    // On nettoie le suffixe ". N" si la série est détectée séparément
    const raw = _getAll(dc, 'title')[0] ?? '';
    return raw.replace(/\.\s*\d+$/, '').trim();
}

function _extractAuthors(dc) {
    // dc:creator contient "Nom, Prénom (dates)" — on retire les dates entre parenthèses
    return _getAll(dc, 'creator')
        .map(author => author.replace(/\s*\(\d{4}[-–]\d{0,4}\)/, '').trim())
        .join(', ');
}

function _extractPublisher(dc) {
    return _getAll(dc, 'publisher')[0] ?? '';
}

function _extractSeries(dc) {
    // La série est souvent dans dc:description sous la forme "Série : Astérix ; 1"
    // ou "Collection : ..."
    const descriptions = _getAll(dc, 'description');

    for (const desc of descriptions) {
        const serieMatch = desc.match(/(?:Série|Collection)\s*:\s*([^;]+)/i);
        if (serieMatch) return serieMatch[1].trim();
    }
    return '';
}

/**
 * Construit l'URL de couverture BnF.
 * La BnF expose des couvertures via un endpoint dédié interrogeable par ISBN.
 * Format : https://catalogue.bnf.fr/couverture?isbn=XXX&quelle=famille&CNT=QU&VHN=famille
 *
 * Alternative : récupérer l'ARK dans dc:identifier et construire l'URL IIIF,
 * mais l'endpoint ISBN est plus simple et couvre la majorité des cas.
 */
function _extractCoverUrl(dc, isbn) {
    // Vérifie d'abord si la BnF signale une image dans les identifiants
    const identifiers = _getAll(dc, 'identifier');
    const hasArk = identifiers.some(id => id.startsWith('ark:'));

    if (!hasArk) return null;

    // Endpoint couverture BnF par ISBN — retourne une image si disponible,
    // sinon une image transparente 1x1 (pas d'erreur HTTP)
    return `${BNF_COVER_BASE}?isbn=${isbn}&quelle=famille&CNT=QU&VHN=famille`;
}
